/**
 * main-product-combina.js
 * Carrusel "Combina Perfecto Con" + botón rápido de agregar al carrito.
 * Adaptado de main-product-extras.js (Fruto Salvaje) → clases .cb-combina
 * para main-product.liquid (Carblock). Auto-init por IntersectionObserver.
 */
if (!customElements.get('main-product-combina')) {
  class MainProductCombina extends HTMLElement {
    connectedCallback() {
      // IMPORTANTE: este elemento usa `display: contents` en CSS para no
      // romper el grid de la sección. Eso significa que no tiene caja de
      // layout propia, así que IntersectionObserver nunca lo detecta como
      // intersectando si se observa `this` directamente. Observamos un
      // hijo real (.page-width) que sí tiene dimensiones — mismo fix que
      // ya se aplicó en main-product-extras.js (Fruto Salvaje).
      const target = this.querySelector('.page-width') || this;

      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.init();
        },
        { rootMargin: '300px' }
      );
      this._io.observe(target);

      this._fallbackTimer = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 4000);
    }

    disconnectedCallback() {
      this._io?.disconnect();
      clearTimeout(this._fallbackTimer);
      this._resizeHandler && window.removeEventListener('resize', this._resizeHandler);
      this._combinaObserver && this._combinaObserver.disconnect();
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallbackTimer);
      this.initCombina();
    }

    initCombina() {
      const checkIconSVG =
        '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 12 4 10" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const addToCart = (variantId, btn) => {
        if (!variantId || btn.disabled) return;
        const originalHTML = btn.innerHTML;
        btn.classList.add('is-loading');
        btn.disabled = true;
        btn.innerHTML = '...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        })
          .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
          .then((result) => {
            btn.classList.remove('is-loading');
            if (!result.ok || result.data.status) {
              btn.disabled = false;
              btn.innerHTML = originalHTML;
              return;
            }
            btn.classList.add('is-added');
            btn.innerHTML = checkIconSVG + ' ¡Agregado!';

            const cartDrawerEl = document.querySelector('cart-drawer');
            const cartNotificationEl = document.querySelector('cart-notification');
            const sectionIds = [
              'cart-drawer',
              'cart-icon-bubble',
              'cart-live-region-text',
              'cart-notification-product',
              'cart-notification-button',
            ];

            fetch('/?sections=' + sectionIds.join(','))
              .then((r) => r.json())
              .then((sections) => {
                if (sections['cart-icon-bubble']) {
                  const bubbleWrap = document.getElementById('cart-icon-bubble');
                  if (bubbleWrap) {
                    const temp = document.createElement('div');
                    temp.innerHTML = sections['cart-icon-bubble'];
                    const newBubble = temp.querySelector('.shopify-section')
                      ? temp.querySelector('.shopify-section').innerHTML
                      : temp.innerHTML;
                    bubbleWrap.innerHTML = newBubble;
                  }
                }

                if (cartDrawerEl && sections['cart-drawer']) {
                  const temp = document.createElement('div');
                  temp.innerHTML = sections['cart-drawer'];
                  const newDrawerInner = temp.querySelector('cart-drawer');

                  // Sincroniza también el className (no solo innerHTML):
                  // Dawn usa la clase "is-empty" en <cart-drawer> para
                  // decidir con CSS si oculta el header. Sin esto, el
                  // drawer puede quedar visualmente "congelado" en estado
                  // vacío aunque el contenido ya tenga productos.
                  if (newDrawerInner) {
                    cartDrawerEl.className = newDrawerInner.className;
                    cartDrawerEl.innerHTML = newDrawerInner.innerHTML;
                  }

                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      if (typeof cartDrawerEl.open === 'function') {
                        cartDrawerEl.open();
                      } else {
                        cartDrawerEl.classList.remove('drawer');
                        cartDrawerEl.removeAttribute('hidden');
                      }
                    });
                  });
                } else if (cartNotificationEl && typeof cartNotificationEl.renderContents === 'function') {
                  cartNotificationEl.renderContents(result.data);
                } else {
                  window.location.href = '/cart';
                }
              })
              .catch(() => {
                window.location.href = '/cart';
              });

            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

            setTimeout(() => {
              btn.classList.remove('is-added');
              btn.disabled = false;
              btn.innerHTML = originalHTML;
            }, 2500);
          })
          .catch(() => {
            btn.classList.remove('is-loading');
            btn.disabled = false;
            btn.innerHTML = originalHTML;
          });
      };

      const initOne = (combinaEl) => {
        if (combinaEl.dataset.cbInit) return;
        combinaEl.dataset.cbInit = '1';

        const track = combinaEl.querySelector('.cb-combina__track');
        const dotsEl = combinaEl.querySelector('.cb-combina__dots');
        const prevBtn = combinaEl.querySelector('.cb-combina__arrow--prev');
        const nextBtn = combinaEl.querySelector('.cb-combina__arrow--next');
        if (!track) return;

        const cardEls = track.querySelectorAll('.cb-combina-card');
        const total = cardEls.length;
        if (!total) return;
        let currentIdx = 0;
        const getVisible = () => 1;
        const maxIdx = () => Math.max(0, total - getVisible());
        const getCardWidth = () =>
          cardEls[0] ? cardEls[0].getBoundingClientRect().width + 12 : 0;

        const updateDots = () => {
          if (!dotsEl) return;
          const activePage = Math.floor(currentIdx / getVisible());
          dotsEl
            .querySelectorAll('.cb-combina__dot')
            .forEach((d, i) => d.classList.toggle('is-active', i === activePage));
        };

        const goTo = (idx) => {
          const max = maxIdx();
          currentIdx = Math.max(0, Math.min(idx, max));
          track.style.transform = 'translateX(-' + currentIdx * getCardWidth() + 'px)';
          if (prevBtn) prevBtn.disabled = currentIdx === 0;
          if (nextBtn) nextBtn.disabled = currentIdx >= max;
          updateDots();
        };

        const buildDots = () => {
          if (!dotsEl) return;
          const pages = Math.ceil(total / getVisible());
          dotsEl.innerHTML = '';
          if (pages <= 1) return;
          for (let i = 0; i < pages; i++) {
            const dot = document.createElement('button');
            dot.className = 'cb-combina__dot' + (i === 0 ? ' is-active' : '');
            dot.setAttribute('aria-label', 'Página ' + (i + 1));
            dot.addEventListener('click', () => goTo(i * getVisible()));
            dotsEl.appendChild(dot);
          }
        };

        buildDots();
        if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIdx - 1));
        if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIdx + 1));

        let touchStartX = 0;
        track.addEventListener('touchstart', (e) => (touchStartX = e.touches[0].clientX), {
          passive: true,
        });
        track.addEventListener(
          'touchend',
          (e) => {
            const diff = touchStartX - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 40) goTo(diff > 0 ? currentIdx + 1 : currentIdx - 1);
          },
          { passive: true }
        );

        let resizeTimer;
        this._resizeHandler = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            buildDots();
            goTo(0);
          }, 200);
        };
        window.addEventListener('resize', this._resizeHandler, { passive: true });

        goTo(0);

        track.addEventListener('click', (e) => {
          const btn = e.target.closest('.cb-combina-card__btn');
          if (!btn) return;
          e.preventDefault();
          addToCart(btn.getAttribute('data-variant-id'), btn);
        });
      };

      this.querySelectorAll('.cb-combina').forEach(initOne);

      const wrapper = this.querySelector('product-recommendations.cb-combina-wrapper');
      if (wrapper) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((m) => {
            m.addedNodes.forEach((node) => {
              if (node.nodeType === 1 && node.classList && node.classList.contains('cb-combina')) {
                initOne(node);
              }
            });
          });
        });
        observer.observe(wrapper, { childList: true });
        this._combinaObserver = observer;
      }

      document.addEventListener('shopify:section:load', () => {
        this.querySelectorAll('.cb-combina').forEach(initOne);
      });
    }
  }

  customElements.define('main-product-combina', MainProductCombina);
}
if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this._io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this._io.disconnect();
          this.init();
        },
        { rootMargin: '200px' }
      );
      this._io.observe(this);

      // Fallback por si el navegador no dispara el observer a tiempo
      this._fallback = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 1500);
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallback);

      this.thumbs = Array.from(this.querySelectorAll('.product-gallery__thumb'));
      this.slides = Array.from(this.querySelectorAll('.product-gallery__main-slide'));

      if (this.thumbs.length === 0 || this.slides.length === 0) return;

      this._onThumbClick = this._onThumbClick.bind(this);
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', this._onThumbClick, { passive: true });
      });
    }

    _onThumbClick(event) {
      const index = Number(event.currentTarget.dataset.thumbIndex);
      this._setActive(index);
    }

    _setActive(index) {
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });
    }

    disconnectedCallback() {
      this._io?.disconnect();
      clearTimeout(this._fallback);
    }
  }

  customElements.define('product-gallery', ProductGallery);
}

if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      const targetId = this.dataset.observeTarget;
      this.target = targetId ? document.getElementById(targetId) : null;

      if (!this.target) {
        // Sin botón original que observar, no hay nada que mostrar/ocultar
        return;
      }

      this._io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          // Se muestra cuando el botón original YA NO es visible (salió por abajo al hacer scroll)
          const shouldShow = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', shouldShow);
        },
        { threshold: 0 }
      );
      this._io.observe(this.target);
    }

    disconnectedCallback() {
      this._io?.disconnect();
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}